/**
 * Credit purchases waiting on Connect (`docs/28_UNIFIED_BILLING.md` §5).
 *
 * A purchase lands here when it could not finish at once: the client's first,
 * one over its limit, or one the vendor account has no room for. The money is
 * already held from the client's wallet. Staff then either
 *
 * - load the vendor account in the vendor's portal and record the vendor's
 *   transaction id — Connect adds the credits once the account's balance shows
 *   the load (on "Check now", and every hour);
 * - add the credits at once, where the vendor account already has room (a
 *   load nobody saw needs a reason);
 * - or return the money, with a reason the client never reads.
 *
 * Every action is made once: the server checks the purchase's state under a
 * lock, so a second press finds it moved on.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DetailField,
  DetailGrid,
  Drawer,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  QueryPending,
  Select,
  StatusChip,
  api,
  formatDateTime,
  formatMoney,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

import { refusalText } from './api-refusal';
import { formatMetered } from './ai-card';
import { STATE_LABELS, STATE_TONES, billingKeys, type StaffPurchase } from './billing-card';

interface PurchaseDetail extends StaffPurchase {
  connection: { public_id: string; label: string | null; account: string | null } | null;
  headroom: number | null;
}

const ROUTE_LABELS = {
  instant: 'Added at once',
  first_purchase: 'First purchase',
  over_limit: 'Over the client’s limit',
  needs_load: 'Vendor account needs loading',
};

export function CreditPurchasesPage() {
  const [state, setState] = useState<'open' | 'all'>('open');
  const [params, setParams] = useSearchParams();
  const openId = params.get('open');
  const list = useQuery({
    queryKey: billingKeys.purchases(state),
    queryFn: () => api.get<{ purchases: StaffPurchase[]; has_more: boolean }>('/billing/purchases', { params: { state } }),
  });
  const open = (id: string | null) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (id) next.set('open', id);
      else next.delete('open');
      return next;
    });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Credit purchases"
        description="Clients' SMS credit purchases waiting on Connect. The money is held from the client's wallet until the credits are added or it is returned."
        actions={
          <Select
            aria-label="Show"
            size="sm"
            value={state}
            onChange={(e) => setState(e.target.value as 'open' | 'all')}
            options={[
              { value: 'open', label: 'Waiting on Connect' },
              { value: 'all', label: 'All purchases' },
            ]}
          />
        }
      />
      {list.isLoading ? (
        <LoadingState label="Loading purchases" />
      ) : list.isError || !list.data ? (
        <ErrorState onRetry={() => void list.refetch()} />
      ) : list.data.purchases.length === 0 ? (
        <Card>
          <EmptyState title="Nothing waiting" description="Purchases that need Connect appear here." />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-xs font-medium text-muted">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2 text-right">Credits</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2">Why</th>
                <th className="px-3 py-2">State</th>
              </tr>
            </thead>
            <tbody>
              {list.data.purchases.map((purchase) => (
                <tr
                  key={purchase.public_id}
                  className="cursor-pointer border-t border-line hover:bg-surface-2"
                  onClick={() => open(purchase.public_id)}
                >
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{formatDateTime(purchase.at)}</td>
                  <td className="px-3 py-2">{purchase.client?.name}</td>
                  <td className="px-3 py-2 text-right tabular">{formatNumber(purchase.quantity)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatMoney(purchase.amount)}</td>
                  <td className="px-3 py-2 text-xs text-muted">{ROUTE_LABELS[purchase.route]}</td>
                  <td className="px-3 py-2">
                    <StatusChip value={purchase.state} labels={STATE_LABELS} tones={STATE_TONES} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {openId ? <PurchaseDrawer id={openId} onClose={() => open(null)} /> : null}
    </div>
  );
}

function PurchaseDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const canApprove = useCan('messaging.credit_purchase.approve');
  const key = billingKeys.purchase(id);
  const purchase = useQuery({ queryKey: key, queryFn: () => api.get<PurchaseDetail>(`/billing/purchases/${id}`) });
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  // Adding credits with no load recorded, where the vendor account has no
  // room for them: refused by the server unless asked for explicitly.
  const [override, setOverride] = useState(false);
  const act = useMutation({
    mutationFn: ({ action, body }: { action: 'loaded' | 'check' | 'approve' | 'reject'; body?: object }) =>
      api.post<PurchaseDetail>(`/billing/purchases/${id}/${action}`, body ?? {}),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toast.success(STATE_LABELS[data.state]);
    },
    onError: (e) => toast.error('Not done', refusalText(e)),
  });

  return (
    <Drawer open onClose={onClose} title="Credit purchase" width="lg">
      {purchase.data === undefined ? (
        <QueryPending query={purchase} label="Loading the purchase" />
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader
              title={`${formatNumber(purchase.data.quantity)} SMS credits — ${purchase.data.client?.name ?? ''}`}
              actions={<StatusChip value={purchase.data.state} labels={STATE_LABELS} tones={STATE_TONES} />}
            />
            <CardBody>
              {/* Two columns at most: the grid's breakpoints follow the window, not the drawer. */}
              <DetailGrid className="sm:grid-cols-2 lg:grid-cols-2">
                <DetailField label="Paid by the client">{formatMoney(purchase.data.amount)}</DetailField>
                <DetailField label="Why it waits">{ROUTE_LABELS[purchase.data.route]}</DetailField>
                <DetailField label="Load on the vendor">
                  {purchase.data.provider_cost ? formatMetered(purchase.data.provider_cost) : 'Vendor cost not set'}
                </DetailField>
                <DetailField label="Vendor account">
                  {purchase.data.connection?.account ?? purchase.data.connection?.label ?? '—'}
                </DetailField>
                <DetailField label="Balance before">
                  {purchase.data.balance_before
                    ? `${formatMetered(purchase.data.balance_before)} (${formatDateTime(purchase.data.balance_before_at ?? '')})`
                    : 'Not read'}
                </DetailField>
                <DetailField label="Balance last read">
                  {purchase.data.balance_after ? formatMetered(purchase.data.balance_after) : '—'}
                </DetailField>
                <DetailField label="Room at the vendor now">
                  {purchase.data.headroom === null ? 'Unknown' : `${formatNumber(purchase.data.headroom)} credits`}
                </DetailField>
                <DetailField label="Vendor transaction">{purchase.data.provider_reference ?? '—'}</DetailField>
                {purchase.data.note ? <DetailField label="Note">{purchase.data.note}</DetailField> : null}
              </DetailGrid>
            </CardBody>
          </Card>

          {canApprove && purchase.data.state === 'awaiting_provider' ? (
            <Card>
              <CardHeader
                title="Loaded the vendor account"
                description="Load it in the vendor's portal, then give its transaction id. The credits follow once the balance shows it."
              />
              <CardBody className="flex items-end gap-2">
                <Input label="Vendor transaction id" value={reference} onChange={(e) => setReference(e.target.value)} />
                <Button
                  disabled={!reference.trim()}
                  loading={act.isPending}
                  onClick={() => act.mutate({ action: 'loaded', body: { reference: reference.trim() } })}
                >
                  Record the load
                </Button>
              </CardBody>
            </Card>
          ) : null}

          {canApprove && ['awaiting_provider', 'provider_loaded', 'unconfirmed'].includes(purchase.data.state) ? (
            <Card>
              <CardBody className="space-y-3">
                {purchase.data.state === 'awaiting_provider' ? (
                  <Checkbox
                    label="No load is recorded — add the credits anyway"
                    hint="Only when the client's account at the vendor already backs them, or will today. Needs a note. Otherwise load it in the vendor's panel and record the TXN id first."
                    checked={override}
                    onChange={(event) => setOverride(event.target.checked)}
                  />
                ) : null}
                {purchase.data.state !== 'awaiting_provider' ? (
                  <Button variant="secondary" loading={act.isPending} onClick={() => act.mutate({ action: 'check' })}>
                    Check the vendor balance now
                  </Button>
                ) : null}
                <Input
                  label="Note"
                  value={note}
                  hint={
                    purchase.data.state === 'unconfirmed'
                      ? 'Needed to add credits for a load that was not seen, and to return the money.'
                      : 'Needed to return the money. Never shown to the client.'
                  }
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    loading={act.isPending}
                    onClick={() => act.mutate({ action: 'approve', body: { note: note.trim(), override } })}
                  >
                    Add the credits now
                  </Button>
                  <Button
                    variant="danger"
                    disabled={!note.trim()}
                    loading={act.isPending}
                    onClick={() => act.mutate({ action: 'reject', body: { note: note.trim() } })}
                  >
                    Return the money
                  </Button>
                </div>
              </CardBody>
            </Card>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
